/**
 * Optimized WebSocket Hook for Real-Time Updates
 * Prevents unnecessary re-renders and handles connection lifecycle carefully
 */

import { useEffect, useRef, useCallback, useMemo } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '@/stores/useAuthStore'
import { useMarketStore } from '@/stores/useMarketStore'
import { useTradingStore } from '@/stores/useTradingStore'
import { handleWarning } from '@/lib/errorHandler'

interface UseWebSocketOptions {
  autoConnect?: boolean
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Error) => void
}

interface WebSocketState {
  isConnected: boolean
  isConnecting: boolean
  connectionAttempts: number
  lastError: string | null
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { autoConnect = true, onConnect, onDisconnect, onError } = options
  
  // Use refs to prevent re-renders
  const socketRef = useRef<Socket | null>(null)
  const stateRef = useRef<WebSocketState>({
    isConnected: false,
    isConnecting: false,
    connectionAttempts: 0,
    lastError: null
  })
  const reconnectTimeoutRef = useRef<number | null>(null)
  const isUnmountedRef = useRef(false)
  
  // Stable references to prevent re-renders
  const { isAuthenticated } = useAuthStore()
  const updatePrice = useMarketStore(state => state.updatePrice)
  const updatePosition = useTradingStore(state => state.updatePosition)
  const updateBalance = useTradingStore(state => state.updateBalance)

  // Memoize WebSocket URL to prevent reconnections
  const wsUrl = useMemo(() => {
    const url = import.meta.env.VITE_WS_URL || '/'
    // Convert relative path to full WebSocket URL
    if (url.startsWith('/')) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${protocol}//${window.location.host}`
    }
    return url
  }, [])

  // Stable callback references
  const stableOnConnect = useCallback(() => {
    if (isUnmountedRef.current) return
    onConnect?.()
  }, [onConnect])

  const stableOnDisconnect = useCallback(() => {
    if (isUnmountedRef.current) return
    onDisconnect?.()
  }, [onDisconnect])

  const stableOnError = useCallback((error: Error) => {
    if (isUnmountedRef.current) return
    onError?.(error)
  }, [onError])

  /**
   * Clean disconnect function
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (socketRef.current) {
      console.log('🔌 Disconnecting WebSocket...')
      
      // Remove all listeners to prevent memory leaks
      socketRef.current.removeAllListeners()
      socketRef.current.disconnect()
      socketRef.current = null
    }

    stateRef.current = {
      isConnected: false,
      isConnecting: false,
      connectionAttempts: 0,
      lastError: null
    }
  }, [])

  /**
   * Connect with careful state management
   */
  const connect = useCallback(() => {
    // Prevent multiple connection attempts
    if (stateRef.current.isConnecting || stateRef.current.isConnected || isUnmountedRef.current) {
      return
    }

    const sessionId = localStorage.getItem('sessionId')
    if (!sessionId) {
      console.warn('❌ Cannot connect WebSocket: No session ID')
      return
    }

    if (socketRef.current?.connected) {
      console.log('✅ WebSocket already connected')
      return
    }

    console.log('🔌 Connecting WebSocket to:', wsUrl)
    stateRef.current.isConnecting = true
    stateRef.current.connectionAttempts += 1

    const socket = io(wsUrl, {
      auth: { sessionId },
      transports: ['websocket', 'polling'],
      reconnection: false, // We'll handle reconnection manually
      timeout: 10000,
      forceNew: true, // Prevent connection reuse issues
    })

    // Connection success handler
    const handleConnect = () => {
      if (isUnmountedRef.current) return
      
      console.log('✅ WebSocket connected successfully')
      stateRef.current.isConnected = true
      stateRef.current.isConnecting = false
      stateRef.current.connectionAttempts = 0
      stateRef.current.lastError = null
      
      stableOnConnect()
    }

    // Authentication confirmation handler
    const handleAuthenticated = (data: any) => {
      if (isUnmountedRef.current) return
      console.log('🔐 WebSocket authenticated:', data)
    }

    // Disconnection handler
    const handleDisconnect = (reason: string) => {
      if (isUnmountedRef.current) return
      
      console.log('❌ WebSocket disconnected:', reason)
      stateRef.current.isConnected = false
      stateRef.current.isConnecting = false
      
      stableOnDisconnect()
      
      // Auto-reconnect logic (with exponential backoff)
      if (reason !== 'io client disconnect' && stateRef.current.connectionAttempts < 5) {
        const delay = Math.min(1000 * Math.pow(2, stateRef.current.connectionAttempts), 30000)
        console.log(`🔄 Reconnecting in ${delay}ms (attempt ${stateRef.current.connectionAttempts + 1})`)
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!isUnmountedRef.current && isAuthenticated) {
            connect()
          }
        }, delay) as unknown as number
      }
    }

    // Connection error handler
    const handleConnectError = (error: any) => {
      if (isUnmountedRef.current) return
      
      console.error('❌ WebSocket connection error:', error)
      stateRef.current.isConnecting = false
      stateRef.current.lastError = error.message || 'Connection failed'
      
      stableOnError(error)
      
      // Show user-friendly error (throttled)
      if (stateRef.current.connectionAttempts <= 2) {
        handleWarning('Trying to reconnect to real-time updates...')
      }
    }

    // Real-time data handlers (defined outside to avoid hooks violations)
    const handlePriceUpdate = (price: any) => {
      if (isUnmountedRef.current) return
      updatePrice(price.instrument, price)
    }

    const handlePositionUpdate = (position: any) => {
      if (isUnmountedRef.current) return
      updatePosition(position)
      
      // Capital.com WS sometimes sends incomplete PnL for new/updated positions
      // We immediately fetch fresh data from the REST API to guarantee accuracy
      useTradingStore.getState().loadPositions()
      useTradingStore.getState().loadBalance()
    }

    const handleBalanceUpdate = (balance: any) => {
      if (isUnmountedRef.current) return
      updateBalance(balance)
    }

    const handleSubscribed = (data: { instrument: string }) => {
      if (isUnmountedRef.current) return
      console.log(`📊 Subscribed to ${data.instrument}`)
    }

    const handleUnsubscribed = (data: { instrument: string }) => {
      if (isUnmountedRef.current) return
      console.log(`📊 Unsubscribed from ${data.instrument}`)
    }

    // Attach event listeners
    socket.on('connect', handleConnect)
    socket.on('connected', handleAuthenticated)
    socket.on('disconnect', handleDisconnect)
    socket.on('connect_error', handleConnectError)
    socket.on('price:update', handlePriceUpdate)
    socket.on('position:update', handlePositionUpdate)
    socket.on('balance:update', handleBalanceUpdate)
    socket.on('subscribed', handleSubscribed)
    socket.on('unsubscribed', handleUnsubscribed)

    socketRef.current = socket
  }, [wsUrl, isAuthenticated, stableOnConnect, stableOnDisconnect, stableOnError, updatePrice, updatePosition, updateBalance])

  /**
   * Subscribe to price updates (with connection check)
   */
  const subscribeToPrice = useCallback((instrument: string) => {
    if (!socketRef.current?.connected) {
      console.warn('⚠️ Cannot subscribe: WebSocket not connected')
      return false
    }

    console.log(`📊 Subscribing to price updates for ${instrument}`)
    socketRef.current.emit('subscribe:price', instrument)
    return true
  }, [])

  /**
   * Unsubscribe from price updates (with connection check)
   */
  const unsubscribeFromPrice = useCallback((instrument: string) => {
    if (!socketRef.current?.connected) {
      console.warn('⚠️ Cannot unsubscribe: WebSocket not connected')
      return false
    }

    console.log(`📊 Unsubscribing from price updates for ${instrument}`)
    socketRef.current.emit('unsubscribe:price', instrument)
    return true
  }, [])

  /**
   * Get current connection state (without causing re-renders)
   */
  const getConnectionState = useCallback(() => {
    return {
      isConnected: stateRef.current.isConnected,
      isConnecting: stateRef.current.isConnecting,
      connectionAttempts: stateRef.current.connectionAttempts,
      lastError: stateRef.current.lastError
    }
  }, [])

  /**
   * Force reconnection
   */
  const reconnect = useCallback(() => {
    console.log('🔄 Force reconnecting WebSocket...')
    disconnect()
    setTimeout(() => {
      if (!isUnmountedRef.current && isAuthenticated) {
        connect()
      }
    }, 1000)
  }, [disconnect, connect, isAuthenticated])

  // Auto-connect effect (runs only when authentication changes)
  useEffect(() => {
    if (autoConnect && isAuthenticated && !stateRef.current.isConnected && !stateRef.current.isConnecting) {
      console.log('🚀 Auto-connecting WebSocket...')
      connect()
    } else if (!isAuthenticated && (stateRef.current.isConnected || stateRef.current.isConnecting)) {
      console.log('🔒 User logged out, disconnecting WebSocket...')
      disconnect()
    }
  }, [autoConnect, isAuthenticated, connect, disconnect])

  // Cleanup effect
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up WebSocket hook...')
      isUnmountedRef.current = true
      disconnect()
    }
  }, [disconnect])

  // Return stable API (prevents re-renders in consuming components)
  return useMemo(() => ({
    // State getters (use these instead of direct state to prevent re-renders)
    getConnectionState,
    
    // Connection methods
    connect,
    disconnect,
    reconnect,
    
    // Subscription methods
    subscribeToPrice,
    unsubscribeFromPrice,
    
    // Convenience getters (these will cause re-renders, use sparingly)
    get isConnected() { return stateRef.current.isConnected },
    get isConnecting() { return stateRef.current.isConnecting },
  }), [
    getConnectionState,
    connect,
    disconnect,
    reconnect,
    subscribeToPrice,
    unsubscribeFromPrice
  ])
}

export default useWebSocket